import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import autobind from 'autobind-decorator';
import splitDux from 'src/redux/split';

import {
  SliderControlsContainer,
  SliderBar,
  SliderBarCurrentOffsetText,
  SliderBarTicks,
  SliderBarFill,
  SliderButtonContainer,
  SliderLineContainer,
  SliderThumbContainer,
  ButtonCircle,
  InputSlider,
} from './styled';

const mapStateToProps = (state) => ({
  labels: splitDux.selectors.getSplitToolLabels(state),
  splitPercentage: splitDux.selectors.getSplitPercentage(state),
  oneInchPercentageIncrement: splitDux.selectors.getSplitToolOneInchPercentageIncrement(
    state,
  ),
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      handleSplitToolOffsetChange: splitDux.actions.handleSplitToolOffsetChange,
    },
    dispatch,
  );

class SliderControl extends PureComponent {
  static propTypes = {
    handleSplitToolOffsetChange: PropTypes.func.isRequired,
    labels: PropTypes.object.isRequired,
    splitPercentage: PropTypes.number.isRequired,
    oneInchPercentageIncrement: PropTypes.number.isRequired,
  };

  @autobind
  onChangeRange(e) {
    const newRangeValue = e.target.value;
    let newSplitPercentage = newRangeValue / 100;
    if (newSplitPercentage === 1) {
      newSplitPercentage -= this.props.oneInchPercentageIncrement;
    } else if (newSplitPercentage === 0) {
      newSplitPercentage += this.props.oneInchPercentageIncrement;
    }

    this.props.handleSplitToolOffsetChange(newSplitPercentage);
  }

  /**
   * Context about why we need this method:
   * We need the height of the InputSlider to match the height of the SliderControlsContainer.
   * But the InputSlider is rotated 90 degrees so instead we need the width of the
   * InputSlider to match the height of the SliderControlsContainer.
   * We cannot get the height of the SliderControlsContainer through CSS, so we
   * get it through Javascript in this method.
   */
  @autobind
  getSliderControlsContainerHeight() {
    return this.sliderControlsContainerRef
      ? this.sliderControlsContainerRef.clientHeight
      : 0;
  }

  renderSliderBar() {
    const { splitPercentage } = this.props;

    return (
      <SliderBar>
        <SliderBarTicks percentHeight={0.25} />
        <SliderBarTicks percentHeight={0.5} />
        <SliderBarTicks percentHeight={0.75} />
        <SliderBarFill fillPercentage={splitPercentage} />
      </SliderBar>
    );
  }

  renderSliderThumb() {
    const {
      splitPercentage,
      labels: { currentValueLabel },
    } = this.props;

    return (
      <SliderThumbContainer fillPercentage={splitPercentage}>
        <SliderBarCurrentOffsetText data-selenium="sliderBarCurrentOffsetText">
          {currentValueLabel}
        </SliderBarCurrentOffsetText>

        <SliderLineContainer>
          <img
            src={require('src/assets/images/icons-split_slider/line@2x.png')}
            alt="sliderThumbLine"
            draggable={false}
          />
        </SliderLineContainer>

        <SliderButtonContainer>
          <ButtonCircle
            src={require('src/assets/images/icons-split_slider/btn-circle@2x.png')}
            alt="sliderThumbButton"
            draggable={false}
            data-selenium="splitSliderButton"
          />
        </SliderButtonContainer>
      </SliderThumbContainer>
    );
  }

  render() {
    const { splitPercentage } = this.props;

    /*
     * The architecture of this slider is an invisible input html tag of type=range
     * which overlays the SliderBar and SliderThumb components. This is needed because
     * implementing the slider cross-browser on touch devices works best when using the
     * native input html tag. It works better than using pointer and/or touch events.
     */
    return (
      <SliderControlsContainer
        innerRef={(el) => {
          this.sliderControlsContainerRef = el;
        }}
      >
        {this.renderSliderBar()}
        {this.renderSliderThumb()}
        <InputSlider
          data-selenium="splitRangeSlider"
          value={splitPercentage * 100}
          parentHeight={this.getSliderControlsContainerHeight()}
          min={0}
          max={100}
          step={1}
          type="range"
          onChange={this.onChangeRange}
        />
      </SliderControlsContainer>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(SliderControl);
